import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    CheckCircle,
    ArrowForward,
    Facebook,
    Twitter,
    LinkedIn,
    Instagram,
    Add as AddIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';

// Import images
import HeroSecbg from '../../assets/hompageimgs/HeroSecbg.png';
import HeroCard1 from '../../assets/hompageimgs/Hero-Card1.png';
import HeroCard2 from '../../assets/hompageimgs/Hero-Card2.png';
import HeroCard3 from '../../assets/hompageimgs/Hero-Card3.png';
import HeroEllipse1 from '../../assets/hompageimgs/Hero-Ellipse1.png';
import HeroEllipse2 from '../../assets/hompageimgs/Hero-Ellipse2.png';
import HeroEllipse3 from '../../assets/hompageimgs/Hero-Ellipse3.png';
import HeroEllipse4 from '../../assets/hompageimgs/Hero-Ellipse4.png';
import ExploreSec from '../../assets/hompageimgs/ExploreSec.png';
import PerformSecImg1 from '../../assets/hompageimgs/PerformSec-img1.png';
import PerformSecImg2 from '../../assets/hompageimgs/PerformSec-img2.png';
import PerformSecImg3 from '../../assets/hompageimgs/PerformSec-img3.png';
import Why360Icon1 from '../../assets/hompageimgs/Why360-icon1.png';
import Why360Icon2 from '../../assets/hompageimgs/Why360-icon2.png';
import Why360Icon3 from '../../assets/hompageimgs/Why360-icon3.png';
import Why360Icon4 from '../../assets/hompageimgs/Why360-icon4.png';
import BuildSecIcon1 from '../../assets/hompageimgs/buildSec-icon1.png';
import BuildSecIcon2 from '../../assets/hompageimgs/buildSec-icon2.png';
import BuildSecIcon3 from '../../assets/hompageimgs/buildSec-icon3.png';
import BuildSecIcon4 from '../../assets/hompageimgs/buildSec-icon4.png';
import BuildSecRi from '../../assets/hompageimgs/buildSec-ri.png';
import CoreSecIcon1 from '../../assets/hompageimgs/coreSec-icon1.png';
import CoreSecIcon2 from '../../assets/hompageimgs/coreSec-icon2.png';
import CoreSecIcon3 from '../../assets/hompageimgs/coreSec-icon3.png';
import CoreSecIcon4 from '../../assets/hompageimgs/coreSec-icon4.png';
import CoreSecIcon5 from '../../assets/hompageimgs/coreSec-icon5.png';
import CoreSecIcon6 from '../../assets/hompageimgs/coreSec-icon6.png';
import Leader360Logo from '../../assets/hompageimgs/leader360-logo.png';
import Dashboard from '../../assets/hompageimgs/Dashboard.png';
import LocationOn from '@mui/icons-material/LocationOn';
import Phone from '@mui/icons-material/Phone';
import Email from '@mui/icons-material/Email';

// Color palette
const colors = {
    primaryDark: '#0F172A',
    navyDark: '#1A0B2E',
    accentYellow: '#F2C94C',
    accentGreen: '#518B51',
    oliveGreen: '#6A8E4E',
    lightGreen: '#DDF3E8',
    lightBg: '#F8FAFC',
    white: '#FFFFFF',
    textGray: '#64748B',
    textDark: '#1E293B',
    blueBg: '#1E3A5F',
};

// Smooth scroll handler
const smoothScrollTo = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
        const headerOffset = 80; // Account for fixed header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
        });
    }
};

// Navigation Component
const Navigation = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const navLinks = ['Home', 'About', 'Features', 'Pricing', 'Contact Us'];

    const handleNavClick = (e: React.MouseEvent, link: string) => {
        e.preventDefault();
        // Map "Contact Us" to "contactus" ID
        const targetId = link.toLowerCase().replace(' ', '');
        smoothScrollTo(targetId);
        if (mobileOpen) setMobileOpen(false);
    };

    return (
        <Box
            component="nav"
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
                    {/* Logo */}
                    <Box
                        component="a"
                        href="#home"
                        onClick={(e: React.MouseEvent) => handleNavClick(e, 'home')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            textDecoration: 'none',
                        }}
                    >
                        <Box
                            component="img"
                            src="/leader360-logo.png"
                            alt="Leader 360"
                            sx={{ height: 40 }}
                        />
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 800,
                                color: colors.primaryDark,
                            }}
                        >

                        </Typography>
                    </Box>

                    {/* Desktop Navigation */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {navLinks.map((link) => (
                                <Typography
                                    key={link}
                                    component="a"
                                    href={`#${link.toLowerCase()}`}
                                    onClick={(e: React.MouseEvent) => handleNavClick(e, link)}
                                    sx={{
                                        color: colors.textDark,
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        fontSize: 15,
                                        cursor: 'pointer',
                                        transition: 'color 0.3s',
                                        '&:hover': { color: colors.accentGreen },
                                    }}
                                >
                                    {link}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {/* CTA Buttons */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: colors.oliveGreen,
                                    color: colors.white,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    '&:hover': { bgcolor: '#5A7E3E' },
                                }}
                                href="#contact"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    smoothScrollTo('contactus');
                                }}
                            >
                                Request Demo
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: colors.navyDark,
                                    color: colors.white,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 3,
                                    '&:hover': { bgcolor: '#2A1B3E' },
                                }}
                                href="/login"
                            >
                                Login
                            </Button>
                        </Box>
                    )}

                    {/* Mobile Menu Button */}
                    {isMobile && (
                        <IconButton onClick={() => setMobileOpen(true)}>
                            <MenuIcon />
                        </IconButton>
                    )}
                </Box>
            </Container>

            {/* Mobile Drawer */}
            <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
                <Box sx={{ width: 280, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <IconButton onClick={() => setMobileOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <List>
                        {navLinks.map((link) => (
                            <ListItem
                                key={link}
                                component="a"
                                href={`#${link.toLowerCase()}`}
                                onClick={(e: React.MouseEvent) => handleNavClick(e, link)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <ListItemText primary={link} />
                            </ListItem>
                        ))}
                    </List>
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button variant="outlined" fullWidth href="/login">
                            Login
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ bgcolor: colors.accentYellow, color: colors.primaryDark }}
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                smoothScrollTo('contactus');
                                setMobileOpen(false);
                            }}
                        >
                            Request Demo
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

// Hero Section
const HeroSection = () => {
    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box
            id="home"
            sx={{
                minHeight: '100vh',
                pt: { xs: 12, md: 16 },
                pb: { xs: 8, md: 12 },
                background: `url(${HeroSecbg}) no-repeat center center`,
                backgroundSize: 'cover',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Decorative Ellipses */}
            <Box
                component="img"
                src={HeroEllipse1}
                alt=""
                sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', top: '20%', left: '10%', height: 15, width: 15, opacity: 0.9, animation: 'float 6s ease-in-out infinite' }}
            />
            <Box
                component="img"
                src={HeroEllipse2}
                alt=""
                sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: '0', top: '20%', right: '0', left: '42%', height: 40, width: 40, opacity: 0.9, animation: 'float 8s ease-in-out infinite' }}
            />
            <Box
                component="img"
                src={HeroEllipse3}
                alt=""
                sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: '2%', right: '12%', width: 50, opacity: 0.9, animation: 'float 7s ease-in-out infinite' }}
            />
            <Box
                component="img"
                src={HeroEllipse4}
                alt=""
                sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: '0', top: '35%', right: '0', left: '25%', width: 110, opacity: 0.9 }}
            />

            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    {/* Left Content */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: 32, md: 38, lg: 44 },
                                fontWeight: 800,
                                color: colors.primaryDark,
                                lineHeight: 1.2,
                                mb: 3,
                            }}
                        >
                            One Platform to Engage{' '}
                            <Box
                                component="span"

                            >
                                Citizens & Resolve
                            </Box>{' '}
                            Grievances at Scale
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: 16, md: 18 },
                                color: colors.textGray,
                                mb: 4,
                                lineHeight: 1.8,
                            }}
                        >
                            A secure, multi-tenant SaaS platform for citizen data management, Message campaigns, and grievance tracking.
                        </Typography>

                        {/* CTA Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{
                                    bgcolor: colors.accentYellow,
                                    color: colors.primaryDark,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: 16,
                                    boxShadow: '0 8px 24px rgba(242, 201, 76, 0.4)',
                                    '&:hover': {
                                        bgcolor: '#E5BC45',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 32px rgba(242, 201, 76, 0.5)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    smoothScrollTo('contactus');
                                }}
                            >
                                Request Demo
                            </Button>
                            <Button
                                variant="text"
                                size="large"
                                endIcon={<ArrowForward />}
                                sx={{
                                    color: colors.primaryDark,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 16,
                                    '&:hover': { color: colors.accentGreen },
                                }}
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    smoothScrollTo('features');
                                }}
                            >
                                Explore Features
                            </Button>
                        </Box>

                        {/* Feature Checklist */}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {['Citizen Management', 'Grievances Tracking', 'Campaign Management'].map((feature) => (
                                <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle sx={{ color: colors.accentGreen, fontSize: 20 }} />
                                    <Typography sx={{ color: colors.textDark, fontWeight: 500, fontSize: 14 }}>
                                        {feature}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Right Content - Dashboard Cards */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: { xs: 300, md: 500 },
                            }}
                        >
                            {/* Card 1 - Top Left */}
                            <Box
                                component="img"
                                src={HeroCard1}
                                alt="Total Citizens"
                                sx={{
                                    position: 'absolute',
                                    top: { xs: 0, md: 20 },
                                    left: { xs: 0, md: 0 },
                                    height: { xs: 140, md: 220 },
                                    width: { xs: 140, md: 240 },
                                    borderRadius: 3,
                                    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                                    animation: 'slideUp 0.8s ease-out',
                                }}
                            />
                            {/* Card 2 - Center */}
                            <Box
                                component="img"
                                src={HeroCard2}
                                alt="Campaign Analysis"
                                sx={{
                                    position: 'absolute',
                                    top: { xs: 80, md: 120 },
                                    right: { xs: 0, md: 20 },
                                    width: { xs: 180, md: 285 },
                                    borderRadius: 3,
                                    boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
                                    animation: 'slideUp 1s ease-out',
                                    zIndex: 2,
                                }}
                            />
                            {/* Card 3 - Bottom */}
                            <Box
                                component="img"
                                src={HeroCard3}
                                alt="Resolved Grievances"
                                sx={{
                                    position: 'absolute',
                                    bottom: { xs: 0, md: 2 },
                                    left: { xs: 40, md: 60 },
                                    width: { xs: 160, md: 290 },
                                    borderRadius: 3,
                                    boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
                                    animation: 'slideUp 1.2s ease-out',
                                    zIndex: 1,
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* CSS Animations */}
            <style>
                {`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                    }
                    @keyframes slideUp {
                        from { opacity: 0; transform: translateY(30px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                `}
            </style>
        </Box>
    );
};

// Built to Simplify Section
const BuildSection = () => {
    const features = [
        { icon: BuildSecIcon1, title: 'Centralized Citizen Data', desc: 'Store and manage large databases with advanced filtering.' },
        { icon: BuildSecIcon2, title: 'Smart Communication', desc: 'Run WhatsApp/Mail campaigns with performance tracking.' },
        { icon: BuildSecIcon3, title: 'Grievance Management', desc: 'Track citizen requests from creation to resolution.' },
        { icon: BuildSecIcon4, title: 'Actionable Insights', desc: 'Monitor impact with real-time analytics.' },
    ];

    return (
        <Box id="about" sx={{ py: { xs: 8, md: 8 }, bgcolor: colors.white, position: 'relative' }}>
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">
                    {/* Left - Feature Cards */}
                    <Grid size={{ xs: 12, md: 6 }} sx={{ position: 'relative' }}>
                        <Box
                            component="img"
                            src={HeroEllipse4}
                            alt=""
                            sx={{ display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: '0', top: '18%', right: '0', left: '5%', width: 110, opacity: 0.6, zIndex: 0 }}
                        />
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: 28, md: 54 },
                                fontWeight: 800,
                                fontFamily: '"Lexend Deca", "Poppins"',
                                color: colors.primaryDark,
                                mb: 2,
                            }}
                        >
                            Built to Simplify Citizens Engagement
                        </Typography>
                        <Typography sx={{ color: colors.textGray, mb: 2, fontSize: 16 }}>
                            Manage citizens, communication, and grievances from one secure platform.
                        </Typography>

                        <Grid container spacing={3}>
                            {features.map((feature, index) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                                    <Card
                                        sx={{
                                            p: 3,
                                            height: '100%',
                                            border: '1px solid #E2E8F0',
                                            borderRadius: 3,
                                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.14)',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                                                transform: 'translateY(-5px)',
                                                borderColor: colors.accentGreen,
                                            },
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.icon}
                                            alt={feature.title}
                                            sx={{ width: 48, height: 48, mb: 2 }}
                                        />
                                        <Typography sx={{ fontWeight: 700, color: colors.primaryDark, mb: 1, fontSize: 16 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography sx={{ color: colors.textGray, fontSize: 14, lineHeight: 1.6 }}>
                                            {feature.desc}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Right - Dashboard Image */}
                    <Grid size={{ xs: 12, md: 6 }} sx={{ position: 'absolute', right: '0', display: { xs: 'none', md: 'block' } }}>
                        <Box
                            component="img"
                            src={BuildSecRi}
                            alt="Citizen Request Management Dashboard"
                            sx={{
                                width: '100%',
                                height: '95vh',
                                borderRadius: 4,
                                boxShadow: '0 30px 80px rgba(0,0,0,0.12)',
                            }}
                        />
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

// Core Services Section
const CoreServicesSection = () => {
    const services = [
        { icon: CoreSecIcon1, title: 'Multi Tenant Architecture', desc: 'Each organization operates in a fully isolated environment with independent data, campaigns, and settings.' },
        { icon: CoreSecIcon2, title: 'Citizen Data Management', desc: 'Upload and manage large-scale citizen records with advanced search, filters, and custom attributes.' },
        { icon: CoreSecIcon3, title: 'Campaign Management', desc: 'Create, schedule, and track WhatsApp campaigns using tenant-owned credentials and approved templates.' },
        { icon: CoreSecIcon4, title: 'Grievance Tracking', desc: 'Manage citizen requests from creation to resolution with assignment, priority, and full audit history.' },
        { icon: CoreSecIcon5, title: 'Role Based Access', desc: 'Control access with Admin and Sub-Admin roles with configurable permissions.' },
        { icon: CoreSecIcon6, title: 'Analytics & Reports', desc: 'Gain visibility into campaign performance, grievance resolution, and team productivity.' },
    ];

    return (
        <Box
            id="features"
            sx={{
                py: { xs: 4, md: 4 },
                bgcolor: colors.white,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Background Shape */}
            <Box
                component="img"
                src={ExploreSec}
                alt=""
                sx={{
                    position: 'absolute',
                    top: '-15%',
                    right: '0%',
                    width: '25%',
                    opacity: 0.6,
                    zIndex: 0,
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: 28, md: 40 },
                            fontWeight: 800,
                            color: colors.primaryDark,
                            mb: 1,
                        }}
                    >
                        Explore Our Core
                    </Typography>
                    <Typography variant="h2"
                        sx={{
                            fontSize: { xs: 28, md: 40 },
                            fontWeight: 800,
                            color: colors.accentGreen,
                            mb: 1,
                        }}
                    >
                        Services
                    </Typography>
                    <Typography sx={{ color: colors.textGray, fontSize: 16, maxWidth: 600, mx: 'auto' }}>
                        A complete, secure, and scalable SaaS platform built for real-world civic workflows.
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {services.map((service, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <Card
                                sx={{
                                    p: 4,
                                    height: '100%',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 10px 30px rgba(30, 58, 95, 0.13)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        border: '1px solid #E2E8F0',
                                        boxShadow: '0 10px 30px rgba(30, 58, 95, 0.3)',
                                        background: 'linear-gradient(90deg, rgba(20, 54, 99, 1) 0%, rgba(39, 109, 202, 1) 100%)',
                                        '& .MuiTypography-root': {
                                            color: colors.white,
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 3,
                                        bgcolor: colors.accentGreen + '15',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={service.icon}
                                        alt={service.title}
                                        sx={{
                                            width: 45,
                                            height: 45,
                                        }}
                                    />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: colors.primaryDark, mb: 1.5, fontSize: 18 }}>
                                    {service.title}
                                </Typography>
                                <Typography sx={{ color: colors.textGray, fontSize: 14, lineHeight: 1.7 }}>
                                    {service.desc}
                                </Typography>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box >
    );
};

// Performance Accordion Component
const PerformanceAccordion = () => {
    const [expanded, setExpanded] = useState<string | false>('panel4');

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const accordionItems = [
        {
            id: 'panel1',
            title: 'Citizen Data Intelligence',
            content: 'Store and manage large-scale citizen records with advanced search, filters, and custom attributes for better outreach.',
        },
        {
            id: 'panel2',
            title: 'Message Campaign Automation',
            content: 'Create, schedule, and track WhatsApp and email campaigns with performance analytics and template management.',
        },
        {
            id: 'panel3',
            title: 'End-to-End Grievance Tracking',
            content: 'Manage citizen requests from creation to resolution with assignment, priority levels, and full audit history.',
        },
        {
            id: 'panel4',
            title: 'Real-Time Analytics',
            content: 'Monitor campaign impact and grievance performance with actionable dashboards and reports.',
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {accordionItems.map((item) => (
                <Accordion
                    key={item.id}
                    expanded={expanded === item.id}
                    onChange={handleChange(item.id)}
                    disableGutters
                    elevation={0}
                    sx={{
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                        borderBottom: '1px solid #E2E8F0',
                        '&:last-of-type': { borderBottom: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={
                            expanded === item.id ? (
                                <RemoveIcon sx={{ color: colors.accentYellow, fontSize: 20 }} />
                            ) : (
                                <AddIcon sx={{ color: colors.accentYellow, fontSize: 20 }} />
                            )
                        }
                        sx={{
                            px: 0,
                            py: 1.5,
                            minHeight: 'unset',
                            '& .MuiAccordionSummary-content': { margin: 0 },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: colors.primaryDark,
                                fontSize: 16,
                            }}
                        >
                            {item.title}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0, pb: 2, pt: 0 }}>
                        <Typography sx={{ color: colors.textGray, fontSize: 14, lineHeight: 1.7 }}>
                            {item.content}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

// Performance Section
const PerformanceSection = () => {
    return (
        <Box sx={{ py: { xs: 4, md: 4 }, bgcolor: colors.lightBg }}>
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="flex-start">
                    {/* Left - Mobile Mockup */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'start', minHeight: { md: 500 } }}>
                            <Box
                                component="img"
                                src={PerformSecImg1}
                                alt="Mobile Dashboard"
                                sx={{
                                    width: { xs: '70%', md: '55%' },
                                    borderRadius: 4,
                                    boxShadow: '0 30px 80px rgba(0,0,0,0.15)',
                                    zIndex: 1,
                                }}
                            />
                            {/* Floating card - Top Right */}
                            <Box
                                component="img"
                                src={PerformSecImg2}
                                alt=""
                                sx={{
                                    position: 'absolute',
                                    top: '15%',
                                    right: { xs: '5%', md: '5%' },
                                    width: { xs: 140, md: 200 },
                                    borderRadius: 3,
                                    boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
                                    zIndex: 2,
                                }}
                            />
                            {/* Floating card - Bottom Right */}
                            <Box
                                component="img"
                                src={PerformSecImg3}
                                alt=""
                                sx={{
                                    position: 'absolute',
                                    bottom: '5%',
                                    right: { xs: '0%', md: '2%' },
                                    width: { xs: 130, md: 180 },
                                    borderRadius: 3,
                                    boxShadow: '0 15px 40px rgba(0,0,0,0.12)',
                                    zIndex: 2,
                                }}
                            />
                        </Box>
                    </Grid>

                    {/* Right - Content */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ pt: { md: 4 } }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: 28, md: 38 },
                                    fontWeight: 700,
                                    fontFamily: '"DM Sans", sans-serif',
                                    color: colors.primaryDark,
                                    mb: 2,
                                    lineHeight: 1.2,
                                }}
                            >
                                Performance That Drives<br />Effective Citizen<br />Engagement
                            </Typography>
                            <Typography sx={{ color: colors.textGray, mb: 3, fontSize: 15, lineHeight: 1.7 }}>
                                Powerful tools designed to manage citizens, communication, and grievances with speed, clarity, and accountability.
                            </Typography>

                            <PerformanceAccordion />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

// Dashboard Section
const DashboardSection = () => {
    return (
        <Box sx={{ py: { xs: 4, md: 4 }, bgcolor: colors.white }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {/* Decorative gradient border */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -8,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: { xs: '95%', md: '90%' },
                            height: '100%',
                            borderRadius: 4,
                            padding: '4px',
                            zIndex: 0,
                        }}
                    />
                    {/* Dashboard Image */}
                    <Box
                        component="img"
                        src={Dashboard}
                        alt="Leader 360 Dashboard"
                        sx={{
                            position: 'relative',
                            width: { xs: '92%', md: '88%' },
                            height: { xs: '92%', md: '600px' },
                            borderRadius: 3,
                            boxShadow: '0 25px 80px rgba(0,0,0,0.15)',
                            zIndex: 1,
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

// Pricing Section
const PricingSection = () => {
    const [isYearly, setIsYearly] = useState(false);

    const plans = [
        {
            name: 'STANDARD',
            // monthlyPrice: '₹ 1 Lakh',
            // yearlyPrice: '₹ 10 Lakhs',
            description: 'Starter / Entry-level',
            features: [
                'Up to 2,50,000 citizen records',
                'Basic citizen data management',
                'Message campaigns (limited)',
                'Grievance creation & tracking',
                'Standard analytics dashboard',
                'Email support',
            ],
            popular: false,
        },
        {
            name: 'PROFESSIONAL',
            // monthlyPrice: '₹ 2 Lakhs',
            // yearlyPrice: '₹ 20 Lakhs',
            description: 'Best for growing',
            features: [
                'Up to 5,00,000 citizen records',
                'Multi-tenant enterprise setup',
                'Advanced role & permission controls',
                'High-volume WhatsApp messaging',
                'SLA-backed grievance resolution',
                'Custom analytics & reports',
                'Dedicated account manager',
                'Onboarding & training support',
            ],
            popular: true,
        },
        {
            name: 'PREMIUM',
            // monthlyPrice: '₹ 3 Lakhs',
            // yearlyPrice: '₹ 30 Lakhs',
            description: 'Large-scale governance',
            features: [
                'Up to 10,00,000 citizen records',
                'Advanced search & segmentation',
                'Division & sub-leader management',
                'Unlimited WhatsApp campaigns',
                'Full grievance lifecycle management',
                'Campaign & grievance analytics',
                'Priority support',
            ],
            popular: false,
        },
    ];

    return (
        <Box id="pricing" sx={{ py: { xs: 4, md: 4 }, bgcolor: colors.white }}>
            <Container maxWidth="lg">
                {/* Header with Toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', mb: 6, flexWrap: 'wrap', gap: 3 }}>
                    <Box>
                        <Typography
                            variant="h2"
                            sx={{
                                fontSize: { xs: 28, md: 40 },
                                fontWeight: 700,
                                color: colors.primaryDark,
                                mb: 1,
                            }}
                        >
                            Flexible Plans for Every Scale
                        </Typography>
                        <Typography sx={{ color: colors.textGray, fontSize: 16 }}>
                            Choose a plan that fits your organization's size and operational needs.
                        </Typography>
                    </Box>

                    {/* Monthly/Yearly Toggle */}
                    {/* <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: colors.lightBg,
                            borderRadius: 3,
                            p: 0.5,
                            border: '1px solid #E2E8F0',
                        }}
                    >
                        <Box
                            onClick={() => setIsYearly(false)}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: 2.5,
                                cursor: 'pointer',
                                bgcolor: !isYearly ? colors.white : 'transparent',
                                boxShadow: !isYearly ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: !isYearly ? colors.primaryDark : colors.textGray,
                                }}
                            >
                                Monthly
                            </Typography>
                        </Box>
                        <Box
                            onClick={() => setIsYearly(true)}
                            sx={{
                                px: 3,
                                py: 1,
                                borderRadius: 2.5,
                                cursor: 'pointer',
                                bgcolor: isYearly ? colors.white : 'transparent',
                                boxShadow: isYearly ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: isYearly ? colors.primaryDark : colors.textGray,
                                }}
                            >
                                Yearly
                            </Typography>
                            <Box
                                sx={{
                                    bgcolor: colors.accentGreen,
                                    color: colors.white,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontSize: 10,
                                    fontWeight: 700,
                                }}
                            >
                                SAVE MORE
                            </Box>
                        </Box>
                    </Box> */}
                </Box>

                <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                    {plans.map((plan, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    // pt: plan.popular ? 2 : 0,
                                }}
                            >
                                {/* Recommended Badge */}
                                {plan.popular && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            bgcolor: colors.primaryDark,
                                            color: colors.white,
                                            px: 5,
                                            py: 0.5,
                                            borderRadius: '0 0 20px 20px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            letterSpacing: 0.5,
                                        }}
                                    >
                                        Recommended
                                    </Box>
                                )}

                                <Card
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 3,
                                        border: plan.popular ? `2px solid ${colors.primaryDark}` : '1px solid #E2E8F0',
                                        bgcolor: colors.white,
                                        boxShadow: plan.popular ? '0 10px 40px rgba(81, 139, 81, 0.15)' : '0 4px 20px rgba(0,0,0,0.05)',
                                        overflow: 'visible',
                                    }}
                                >
                                    {/* Top Section - Name, Price, Description, Button */}
                                    <Box sx={{ p: 4, pb: 3 }}>
                                        <Typography sx={{ fontWeight: 700, color: colors.primaryDark, fontSize: 14, mb: 2 }}>
                                            {plan.name}
                                        </Typography>

                                        {/* <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                                            <Typography sx={{ fontSize: 28, fontWeight: 700, color: colors.primaryDark }}>
                                                {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                                            </Typography>
                                            <Typography sx={{ color: colors.textGray, ml: 0.5, fontSize: 16 }}>
                                                {isYearly ? '/ year' : '/ month'}
                                            </Typography>
                                        </Box> */}

                                        <Typography sx={{ color: colors.textGray, fontSize: 14, mb: 3 }}>
                                            {plan.description}
                                        </Typography>

                                        <Button
                                            variant={plan.popular ? 'contained' : 'outlined'}
                                            fullWidth
                                            sx={{
                                                py: 1.5,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: 15,
                                                bgcolor: plan.popular ? colors.accentGreen : colors.lightGreen,
                                                color: plan.popular ? colors.white : colors.primaryDark,
                                                borderColor: '#E2E8F0',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    bgcolor: plan.popular ? '#3d6b3d' : 'rgba(0,0,0,0.04)',
                                                    borderColor: plan.popular ? colors.accentGreen : '#CBD5E1',
                                                    boxShadow: 'none',
                                                },
                                            }}
                                        >
                                            Get started
                                        </Button>
                                    </Box>

                                    {/* Divider */}
                                    <Box sx={{ borderTop: '1px solid #E2E8F0', mx: 4 }} />

                                    {/* Features Section */}
                                    <Box sx={{ p: 4, pt: 3, flex: 1 }}>
                                        {plan.features.map((feature, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                                <CheckCircle sx={{ color: colors.accentGreen, fontSize: 18, mt: 0.3 }} />
                                                <Typography sx={{ color: colors.textDark, fontSize: 14, lineHeight: 1.5 }}>
                                                    {feature}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Card>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// Why Choose Section
const WhyChooseSection = () => {
    const reasons = [
        {
            icon: Why360Icon1,
            title: 'Unified Platform',
            desc: 'Manage citizens, campaigns, grievances, and analytics from one secure system.',
        },
        {
            icon: Why360Icon2,
            title: 'Operational Control',
            desc: 'Assign roles, manage divisions, track accountability, streamline daily operations.',
        },
        {
            icon: Why360Icon3,
            title: 'Data Security',
            desc: 'Ensure strict tenant isolation, encrypted access, and compliance data protection.',
        },
        {
            icon: Why360Icon4,
            title: 'Scalable Growth',
            desc: 'Handle lakhs of records, high messaging volumes, growing organizational complexity.',
        },
    ];

    return (
        <Box sx={{ py: { xs: 4, md: 4 }, bgcolor: colors.white }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: 28, md: 40 },
                            fontWeight: 700,
                            color: colors.primaryDark,
                            mb: 2,
                            textAlign: 'start',
                            lineHeight: 1.3,
                        }}
                    >
                        Why Organizations Choose{' '} <br />
                        <Box component="span" sx={{ color: colors.accentGreen }}>
                            Leader360
                        </Box>{' '}
                        App
                    </Typography>
                </Box>

                <Grid container spacing={3} justifyContent="center">
                    {reasons.map((reason, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    bgcolor: colors.white,
                                    border: '1px solid #E2E8F0',
                                    borderRadius: 3,
                                    p: 4,
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer',
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                                    '&:hover': {
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={reason.icon}
                                    alt={reason.title}
                                    sx={{ width: 56, height: 56, mb: 2 }}
                                />
                                <Typography sx={{ fontWeight: 700, color: colors.primaryDark, mb: 1.5, fontSize: 16 }}>
                                    {reason.title}
                                </Typography>
                                <Typography sx={{ color: colors.textGray, fontSize: 13, lineHeight: 1.7 }}>
                                    {reason.desc}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// CTA Section (Contact)
const ContactSection = () => {
    return (
        <Box id="contact" sx={{ py: { xs: 8, md: 8 }, bgcolor: colors.white }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        bgcolor: colors.primaryDark,
                        borderRadius: 4,
                        p: { xs: 4, md: 6 },
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Background geometric pattern */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: '50%',
                            opacity: 0.1,
                            background: `radial-gradient(circle at 70% 30%, ${colors.accentGreen} 0%, transparent 50%),
                                        radial-gradient(circle at 90% 70%, ${colors.accentYellow} 0%, transparent 40%)`,
                        }}
                    />

                    <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                        {/* Left Content */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: 28, md: 36 },
                                    fontWeight: 800,
                                    color: colors.white,
                                    mb: 2,
                                }}
                            >
                                Ready to Modernize Citizen Engagement
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, lineHeight: 1.8 }}>
                                Ready to modernize citizen engagement with smart, digital solutions that improve communication, access, and trust.
                            </Typography>
                        </Grid>

                        {/* Right - CTA Buttons */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    endIcon={<ArrowForward />}
                                    sx={{
                                        bgcolor: colors.white,
                                        color: colors.primaryDark,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        px: 4,
                                        py: 1.5,
                                        fontSize: 16,
                                        '&:hover': { bgcolor: '#F1F5F9' },
                                    }}
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        smoothScrollTo('contactus');
                                    }}
                                >
                                    Request Demo
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    endIcon={<ArrowForward />}
                                    sx={{
                                        borderColor: colors.white,
                                        color: colors.white,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        px: 4,
                                        py: 1.5,
                                        fontSize: 16,
                                        '&:hover': { borderColor: colors.white, bgcolor: 'rgba(255,255,255,0.1)' },
                                    }}
                                    href="/login"
                                >
                                    Login
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

// Contact Info Section
const ContactInfoSection = () => {
    const contactItems = [
        {
            icon: <LocationOn sx={{ fontSize: 28, color: colors.accentGreen }} />,
            title: 'Address',
            details: [
                'Sree Varasidhi Projects Pvt. Ltd.',
            ],
        },
        {
            icon: <Phone sx={{ fontSize: 28, color: colors.accentGreen }} />,
            title: 'Phone',
            details: ['+91 94823 58999'],
        },
        {
            icon: <Email sx={{ fontSize: 28, color: colors.accentGreen }} />,
            title: 'Email',
            details: ['info@leader360.com'],
        },
    ];

    return (
        <Box id="contactus" sx={{ py: { xs: 6, md: 8 }, bgcolor: colors.lightBg }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: 28, md: 36 },
                            fontWeight: 700,
                            color: colors.primaryDark,
                            mb: 1,
                        }}
                    >
                        Get in Touch
                    </Typography>
                    <Typography sx={{ color: colors.textGray, fontSize: 16 }}>
                        We'd love to hear from you. Reach out to us anytime.
                    </Typography>
                </Box>

                <Grid container spacing={3} justifyContent="center">
                    {contactItems.map((item, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <Box
                                sx={{
                                    bgcolor: colors.white,
                                    borderRadius: 3,
                                    p: 4,
                                    height: '100%',
                                    textAlign: 'center',
                                    border: '1px solid #E2E8F0',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                                        transform: 'translateY(-4px)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        bgcolor: colors.lightGreen,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: colors.primaryDark,
                                        fontSize: 18,
                                        mb: 1.5,
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                {item.details.map((detail, idx) => (
                                    <Typography
                                        key={idx}
                                        sx={{
                                            color: colors.textGray,
                                            fontSize: 14,
                                            lineHeight: 1.8,
                                        }}
                                    >
                                        {detail}
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

// Footer
const Footer = () => {
    const navLinks = ['Home', 'About', 'Features', 'Pricing', 'Contact Us'];

    return (
        <Box sx={{ py: 3, bgcolor: '#0A0F1C' }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            component="img"
                            src={Leader360Logo}
                            alt="Leader 360"
                            sx={{
                                height: 40,
                                filter: 'brightness(0) invert(1)',
                            }}
                        />

                    </Box>

                    {/* Navigation Links */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {navLinks.map((link) => (
                            <Typography
                                key={link}
                                component="a"
                                href={`#${link.toLowerCase()}`}
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    // Map "Contact Us" to "contactus" ID
                                    const targetId = link.toLowerCase().replace(' ', '');
                                    smoothScrollTo(targetId);
                                }}
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    textDecoration: 'none',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'color 0.2s ease',
                                    '&:hover': { color: colors.white },
                                }}
                            >
                                {link}
                            </Typography>
                        ))}
                    </Box>
                </Box>

                {/* Copyright */}
                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', mt: 3, pt: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                        © {new Date().getFullYear()} Leader360 All rights reserved
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

// Main HomePage Component
const HomePage = () => {
    return (
        <Box sx={{ overflow: 'hidden' }}>
            <Navigation />
            <HeroSection />
            <BuildSection />
            <CoreServicesSection />
            <PerformanceSection />
            <DashboardSection />
            <PricingSection />
            <WhyChooseSection />
            <ContactInfoSection />
            <ContactSection />
            <Footer />
        </Box>
    );
};

export default HomePage;